import { useFormik } from 'formik';
import * as Yup from "yup";
import React, { useState } from 'react'
import { Col, Label, Row } from 'reactstrap';
import { OnboardingStatus } from 'shared/constants';
import { useSelector } from 'react-redux';
import { RootState } from 'index';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import BaseButton from 'Components/BaseButton';
import ErrorTooltip from 'Components/ErrorTooltip';
import Message from 'Components/Message';
import apiGlobal from 'global/api.global';
import { errorToast, successToast } from 'Components/Toasts';
import { useQuery } from 'react-query';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import { queryKeyes } from 'shared/queryKeys';
import ErrorComponent from 'Components/ErrorComponent';
import LoadingSkeletonForm from 'Components/LoadingPopUp';

interface EditFleetPopUpType {
    setRefreshKey_2: any;
    fleetID?: number;
    setFleetId?: (id: number) => void;
    setCreateModalState: (state: boolean) => void;
}

const EditFleetPopUp = ({ setRefreshKey_2, fleetID, setFleetId, setCreateModalState }: EditFleetPopUpType) => {
    const { Vessels } = useSelector((state: RootState) => state.Reporting);
    const [searchString, setSearchString] = useState('');

    /**Get fleet details with Id
     * @param url
     * @param fleetID
     * @returns Fleet details with id and count of vessels
    */
    const {
        data: fleetDetails,
        isLoading,
        isError,
    } = useQuery(
        [queryKeyes.fleets.fleetDetails.key, fleetID],
        async () => await Get(queryKeyes.fleets.fleetDetails.url(fleetID), fleetID),
        {
            enabled: fleetID !== null,
        }
    );

    /**Get initial values for formik
     * if in Edit ie fleetId id is present then set the fleet name and vessel as checked included in fleet
     * if not then plot all userVessel 
     * if a vessel is present in fleet_vessel but not in userVessel then disable the checkbox
     * @returns initial values for formik
    */
    const getInitialValues = () => {
        const checkedVesselIds = fleetDetails?.fleet_vessel?.map((v: any) => v.vessel_id) || [];

        const onboardedVessels = Vessels
            ?.filter((v: any) => v.onboarding_status === OnboardingStatus.ONBOARDED) || [];

        const onboardedVesselIds = new Set(onboardedVessels.map((v: any) => v.id));

        const additionalCheckedVessels = (fleetDetails?.fleet_vessel || [])
            .filter((fv: any) => !onboardedVesselIds.has(fv.vessel_id))
            .map((fv: any) => ({
                fleet_id: fleetID || null,
                vessel_checkbox: true,
                vessel_id: fv.vessel_id,
                vessel_name: fv.vessel_name || '', // fallback name if needed
            }));

        const initialFleetVessel = onboardedVessels
            .sort((a: any, b: any) => a.vessel_name.localeCompare(b.vessel_name))
            .map((vessel: any) => ({
                fleet_id: fleetID || null,
                vessel_checkbox: checkedVesselIds.includes(vessel.id),
                vessel_id: vessel.id,
                vessel_name: vessel.vessel_name,
            }));

        return {
            fleet_name: fleetDetails?.fleet_name || '',
            id: fleetID || null,
            fleet_vessel: [...initialFleetVessel, ...additionalCheckedVessels],
        };
    };


    const FleetValidationSchema = Yup.object().shape({
        fleet_name: Yup.string()
            .trim()
            .required("Fleet name is required")
            .min(2, "Fleet name must be at least 2 characters")
            .max(50, "Fleet name must be at most 50 characters"),
        fleet_vessel: Yup.array().of(
            Yup.object().shape({
                vessel_checkbox: Yup.boolean(),
                vessel_id: Yup.number().required("Vessel ID is required"),
            })
        ).test("at-least-one-selected", "At least one vessel must be selected", (fleet_vessel) => {
            return fleet_vessel?.some((vessel) => vessel.vessel_checkbox);
        }),
    });

    const FleetFormik = useFormik({
        enableReinitialize: true,
        initialValues: getInitialValues(),
        validationSchema: FleetValidationSchema,
        onSubmit: async (values, actions) => {
            actions.setSubmitting(true);

            apiGlobal.post(queryKeyes.fleets.createFleet.url(), values)
                .then(() => {
                    setCreateModalState(false);
                    setFleetId(null);
                    successToast("Fleet Created Successfully!");
                    actions.setSubmitting(false);
                    setRefreshKey_2((prev: any) => prev + 1);
                })
                .catch(err => {
                    console.error(err);
                    errorToast(err?.response?.data?.error || "Fleet creation failed");
                    actions.setSubmitting(false);
                });
        }
    });

    // Search functionality
    const handleSearchTextboxChanges = (searchValue: string) => {
        setSearchString(searchValue);
    };

    const getVesselsToDisplay = () => {
        if (!FleetFormik.values.fleet_vessel) return [];

        // Step 1: Filter by search string
        const filtered = FleetFormik.values.fleet_vessel.filter((vessel: any) =>
            vessel.vessel_name?.toLowerCase().includes(searchString.toLowerCase())
        );

        // Step 2: Sort by checkbox status (checked on top), then by name
        return filtered.sort((a: any, b: any) => {
            if (a.vessel_checkbox !== b.vessel_checkbox) {
                return b.vessel_checkbox ? 1 : -1; // true first
            }
            return a.vessel_name.localeCompare(b.vessel_name); // alphabetical
        });
    };

    return (
        <div className="p-2 ">
            <form onSubmit={FleetFormik.handleSubmit} autoComplete="off">
                {isLoading && <LoadingSkeletonForm
                    layout={[
                        [100, 100],
                        [100, 100],
                        [100, 100],
                    ]}
                />}
                {isError && <ErrorComponent message="Unable to load required data!" />}
                {!isLoading && !isError &&
                    <React.Fragment>
                        <Row className="mb-3">
                            <Col xs={12}>
                                <Label className="asteric mb-0 fw-semibold" htmlFor='fleet_name'>Fleet name</Label>
                                <input
                                    type="text"
                                    id="fleet_name"
                                    name="fleet_name"
                                    className="form-control"
                                    placeholder="Enter fleet name"
                                    {...FleetFormik.getFieldProps('fleet_name')}
                                />
                                {FleetFormik.touched.fleet_name && FleetFormik.errors.fleet_name && (
                                    <ErrorTooltip
                                        message={FleetFormik.errors.fleet_name}
                                        target={`fleet_name`}
                                        open={FleetFormik.errors.fleet_name}
                                    />
                                )}
                            </Col>
                        </Row>

                        {/* Vessels Section */}
                        <Row className="mb-2">
                            <Col xs={12}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <Label>Select vessels</Label>
                                    <div className="search-box position-relative border-glow" style={{ width: '250px' }}>
                                        <div className="search-box position-relative border-glow">
                                            <input
                                                type="text"
                                                className="form-control rounded border max-width-20"
                                                placeholder="Search vessel"
                                                name="search"
                                                value={searchString}
                                                onChange={(e: any) => {
                                                    const value = e.target.value;
                                                    setSearchString(value);
                                                    handleSearchTextboxChanges(value);
                                                }}
                                                onBlur={(e: any) => {
                                                    const value = e.target.value;
                                                    setSearchString(value);
                                                    handleSearchTextboxChanges(value);
                                                }}
                                            />
                                            <i className="bx bx-search search-icon link_color_blue"></i>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Vessel checkboxes - now using filtered results */}
                        <Row className='height-15 overflow-auto'>
                            <Col xs={12}>
                                <div className="p-3">
                                    {getVesselsToDisplay().length > 0 ? (
                                        getVesselsToDisplay().map((vessel) => {
                                            const originalIndex = FleetFormik.values.fleet_vessel?.findIndex(
                                                (v) => v.vessel_id === vessel.vessel_id
                                            );

                                            return (
                                                <div key={vessel.vessel_id} className="form-check mb-2">
                                                    <input
                                                        type="checkbox"
                                                        id={`vessel_${vessel.vessel_id}`}
                                                        name={`fleet_vessel[${originalIndex}].vessel_checkbox`}
                                                        className="form-check-input"
                                                        checked={vessel.vessel_checkbox}
                                                        onChange={FleetFormik.handleChange}
                                                        disabled={!Vessels.some((v: any) => v.id === vessel.vessel_id)}
                                                    />
                                                    <label
                                                        className="form-check-label ms-2"
                                                        htmlFor={`vessel_${vessel.vessel_id}`}
                                                    >
                                                        {vessel.vessel_name}
                                                    </label>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="text-center text-muted py-3">
                                            <i className="bx bx-search-alt-2 fs-1 opacity-50"></i>
                                            <p className="mb-0 mt-2">No vessels found</p>
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                        <Row className='mt-3 p-2' >
                            {FleetFormik.errors.fleet_vessel && (
                                <Message message={FleetFormik.errors.fleet_vessel as string} type="danger" width="30rem" />
                            )}
                        </Row>
                        <Row className='d-flex ms-auto justify_right m-2'>
                            <div>
                                <BaseButton
                                    loading={FleetFormik.isSubmitting}
                                    disabled={!FleetFormik.isValid}
                                    title="Save"
                                    type="submit"
                                    variant="primary"
                                    icon={<i className="bx bx-check-double" />}
                                    loadingIcon={<i className="bx bx-hourglass bx-spin" />}
                                />
                            </div>
                        </Row>
                    </React.Fragment>
                }
            </form >

            {/* Debug info - you can remove this */}
            < FormValuesDebug values={[FleetFormik.values, FleetFormik.errors]} />
        </div >
    )
}

export default EditFleetPopUp